import { useTranslation } from "@redotech/redo-i18n";
import { memo, useState } from "react";
import { RedoTextInput } from "./arbiter-components/input/redo-text-input";
import {
  RedoModal,
  RedoModalButtonProps,
} from "./arbiter-components/modal/redo-modal";
import { Flex } from "./flex";
import { Text } from "./text";
import { getLocalizedViewNameValidationError } from "./view-name-validation";

export const SaveTableViewModal = memo(function SaveTableViewModal({
  cancel,
  save,
  reservedViewNames,
}: {
  save(name: string): Promise<void>;
  cancel(): void;
  reservedViewNames: Set<string>;
}) {
  const { t } = useTranslation();
  const [viewName, setViewName] = useState("");

  const submitDisabledBecauseOfEmptyName =
    viewName.length === 0 ? t("You must name this view") : false;
  const submitDisabledBecauseOfInvalidName =
    getLocalizedViewNameValidationError(t, viewName) ?? false;
  const submitDisabledBecauseOfDuplicateName = reservedViewNames.has(
    viewName.toLowerCase(),
  )
    ? t("A view with this name already exists")
    : false;

  const submitDisabled =
    submitDisabledBecauseOfEmptyName ||
    submitDisabledBecauseOfInvalidName ||
    submitDisabledBecauseOfDuplicateName;

  const primaryButton: RedoModalButtonProps = {
    text: t("Confirm"),
    onClick: async () => {
      await save(viewName);
    },
    disabled: submitDisabled,
  };

  const secondaryButton: RedoModalButtonProps = {
    text: t("Cancel"),
    onClick: cancel,
  };

  return (
    <RedoModal
      buttonPlacement="full"
      onModalCloseRequested={cancel}
      primaryButton={primaryButton}
      secondaryButton={secondaryButton}
      title={t("Save as view")}
    >
      <Flex dir="column" gap="xl">
        <Flex dir="column">
          <Text>{t("Name")}</Text>
          <RedoTextInput setValue={setViewName} size="md" value={viewName} />
        </Flex>
        <div data-web="SaveTableViewModal" />
      </Flex>
    </RedoModal>
  );
});
